import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import FolderIcon from "@mui/icons-material/Folder";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SchoolIcon from "@mui/icons-material/School";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { getMyBurnoutRisk } from "@/api/burnout";
import { getMyHome } from "@/api/home";
import { OnboardingCard } from "@/components/OnboardingCard";
import { PageHeader } from "@/components/PageHeader";
import { PendingHandoffsCard } from "@/components/PendingHandoffsCard";

const RISK_COLOR: Record<string, "success" | "info" | "warning" | "error"> = {
  LOW: "success",
  MEDIUM: "info",
  HIGH: "warning",
  CRITICAL: "error",
};

function initials(name?: string | null): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function MyHomePage() {
  const { t } = useTranslation();

  const home = useQuery({
    queryKey: ["my-home"],
    queryFn: getMyHome,
  });

  const burnout = useQuery({
    queryKey: ["burnout-self"],
    queryFn: () => getMyBurnoutRisk(30),
    staleTime: 5 * 60_000,
  });

  if (home.isLoading) return <CircularProgress />;

  const data = home.data;
  if (!data) {
    return (
      <Box>
        <PageHeader title={t("myHome.title")} subtitle={t("myHome.subtitle")} helpTerm="monEspace" />
        <Alert severity="error">{t("myHome.loadError")}</Alert>
      </Box>
    );
  }

  if (!data.is_practitioner) {
    return (
      <Box>
        <PageHeader title={t("myHome.title")} subtitle={t("myHome.subtitle")} />
        <Alert severity="info">{t("myHome.notAPractitioner")}</Alert>
      </Box>
    );
  }

  const p = data.practitioner!;

  return (
    <Box>
      <PageHeader title={t("myHome.title")} subtitle={t("myHome.subtitle")} />

      {/* Welcome card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              {initials(p.display_name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t("myHome.welcome", { name: p.display_name })}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                {p.rpps && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`RPPS ${p.rpps}`}
                    sx={{ fontFamily: "monospace" }}
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("myHome.organisationsChip", {
                    count: data.organization_count,
                  })}
                />
                {burnout.data &&
                  burnout.data.risk_level !== "LOW" &&
                  burnout.data.shifts_total > 0 && (
                    <Chip
                      size="small"
                      color={RISK_COLOR[burnout.data.risk_level]}
                      icon={<WarningAmberIcon fontSize="small" />}
                      label={t("myHome.burnoutChip", {
                        level: t(`burnout.level.${burnout.data.risk_level}`),
                        score: burnout.data.score,
                      })}
                    />
                  )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<CalendarMonthIcon color="primary" />}
            label={t("myHome.monthShifts")}
            value={data.month_shifts}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<CalendarMonthIcon color="info" />}
            label={t("myHome.monthHours")}
            value={`${data.month_hours.toFixed(1)} h`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<CalendarMonthIcon color="success" />}
            label={t("myHome.yearShifts")}
            value={data.year_shifts}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<CalendarMonthIcon color="success" />}
            label={t("myHome.yearHours")}
            value={`${data.year_hours.toFixed(1)} h`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Onboarding (admin only, hidden when complete) */}
        <Grid size={{ xs: 12 }}>
          <OnboardingCard />
        </Grid>

        {/* Pending handoffs (only renders when there are any) */}
        <Grid size={{ xs: 12 }}>
          <PendingHandoffsCard />
        </Grid>

        {/* Upcoming shifts */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                <EventIcon
                  fontSize="small"
                  sx={{ verticalAlign: "middle", mr: 1, color: "primary.main" }}
                />
                {t("myHome.upcomingTitle")}
              </Typography>
              {data.upcoming_shifts.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  {t("myHome.upcomingEmpty")}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {data.upcoming_shifts.map((s) => (
                    <Paper
                      key={s.assignment_id}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderColor: s.is_cancelled ? "error.main" : "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ flexWrap: "wrap" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {s.date}
                        </Typography>
                        <Chip size="small" variant="outlined" label={s.slot_label} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {s.slot_start} – {s.slot_end}
                        </Typography>
                        {s.is_cancelled && (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={t("myHome.cancelledChip")}
                            sx={{ ml: "auto" }}
                          />
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending actions + Documents */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t("myHome.actionsTitle")}
                </Typography>
                <Stack spacing={1.25}>
                  <ActionRow
                    icon={<SwapHorizIcon />}
                    label={t("myHome.swapIncoming")}
                    count={data.incoming_swap_proposals}
                    to="/exchange"
                  />
                  <ActionRow
                    icon={<HandshakeIcon />}
                    label={t("myHome.coverageRequests")}
                    count={data.open_coverage_requests}
                    to="/my-planning"
                  />
                  <ActionRow
                    icon={<SchoolIcon />}
                    label={t("myHome.expiringDocs")}
                    count={data.expiring_documents}
                    to="/profile"
                    warning={data.expiring_documents > 0}
                  />
                </Stack>
              </CardContent>
            </Card>

            {data.expiring_documents_sample.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    <FolderIcon
                      fontSize="small"
                      sx={{
                        verticalAlign: "middle",
                        mr: 1,
                        color: "warning.main",
                      }}
                    />
                    {t("myHome.expiringSoonTitle")}
                  </Typography>
                  <Stack divider={<Divider />} spacing={1}>
                    {data.expiring_documents_sample.map((d) => (
                      <Box key={d.document_id}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {d.label}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                          <Chip size="small" variant="outlined" label={d.kind} />
                          <Typography variant="caption" color="text.secondary">
                            {t("myHome.expiresIn", { days: d.days_until_expiry })}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}


function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon}
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}


function ActionRow({
  icon,
  label,
  count,
  to,
  warning,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  to: string;
  warning?: boolean;
}) {
  return (
    <Stack
      component={RouterLink}
      to={to}
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        textDecoration: "none",
        color: "text.primary",
        p: 1,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ color: warning && count > 0 ? "warning.main" : "primary.main" }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Chip
        size="small"
        color={count > 0 ? (warning ? "warning" : "primary") : "default"}
        variant={count > 0 ? "filled" : "outlined"}
        label={count}
      />
    </Stack>
  );
}
